import { useCallback, useLayoutEffect, useMemo, useRef, useState } from "react"
import { ChevronDown, ChevronUp, ChevronsUpDown } from "lucide-react"

import { cn } from "@/lib/utils"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"

const DEFAULT_COLUMN_WIDTH = 160

export function DataTableReact({
  columns = [],
  data = [],
  className,
  resizable = false,
  sortable = false,
  minColumnWidth = 150,
  maxColumnWidth,
  wrapCells = false,
  onRowClick,
  sortConfig: externalSortConfig,
  onSortChange,
}) {
  const tableRef = useRef(null)
  const headerRefs = useRef([])
  const [columnWidths, setColumnWidths] = useState([])
  const [internalSortConfig, setInternalSortConfig] = useState({ key: null, direction: null })

  const isControlled = externalSortConfig !== undefined
  const sortConfig = isControlled ? externalSortConfig : internalSortConfig

  const columnKey = columns
    .map((column, index) => column.accessorKey ?? (typeof column.header === "string" ? column.header : index))
    .join("|")

  const columnMinWidths = useMemo(() => (
    columns.map((column) => {
      const explicitMin = (
        (typeof column.minWidth === "number" && Number.isFinite(column.minWidth) && column.minWidth)
        || (typeof column.minSize === "number" && Number.isFinite(column.minSize) && column.minSize)
      )

      return Math.max(minColumnWidth, explicitMin || minColumnWidth)
    })
  ), [columns, minColumnWidth])

  const minTotalWidth = useMemo(() => {
    if (!resizable) return 0

    return columnMinWidths.reduce((sum, width) => sum + width, 0)
  }, [columnMinWidths, resizable])

  const sumWidths = useCallback((widths) => (
    widths.reduce((sum, width) => sum + (Number.isFinite(width) ? width : 0), 0)
  ), [])

  useLayoutEffect(() => {
    if (!resizable || columns.length === 0) return

    setColumnWidths((prev) => {
      const hasValidWidths = prev.length === columns.length && prev.every((width) => typeof width === "number")
      const baseWidths = hasValidWidths
        ? [...prev]
        : columns.map((column, index) => {
          if (typeof column.size === "number" && Number.isFinite(column.size)) {
            return Math.max(columnMinWidths[index] || minColumnWidth, column.size)
          }

          // Measure the widest cell in this column (header + body cells)
          const table = tableRef.current
          if (table) {
            let maxMeasured = 0
            const rows = table.querySelectorAll("tr")
            for (const row of rows) {
              const cell = row.children[index]
              if (cell) {
                const measured = cell.getBoundingClientRect().width
                if (measured && Number.isFinite(measured)) {
                  maxMeasured = Math.max(maxMeasured, Math.round(measured))
                }
              }
            }
            if (maxMeasured > 0) {
              let width = Math.max(columnMinWidths[index] || minColumnWidth, maxMeasured)
              if (typeof maxColumnWidth === "number" && Number.isFinite(maxColumnWidth)) {
                width = Math.min(width, maxColumnWidth)
              }
              return width
            }
          }

          return Math.max(columnMinWidths[index] || minColumnWidth, DEFAULT_COLUMN_WIDTH)
        })

      const clamped = baseWidths.map((w, index) => {
        let result = Math.max(columnMinWidths[index] || minColumnWidth, w)
        if (!hasValidWidths && typeof maxColumnWidth === "number" && Number.isFinite(maxColumnWidth)) {
          result = Math.min(result, maxColumnWidth)
        }
        return result
      })
      const total = sumWidths(clamped)
      if (!minTotalWidth || total >= minTotalWidth) return clamped

      return clamped.map((width, index) => Math.max(columnMinWidths[index] || minColumnWidth, width))
    })
  }, [columnKey, columnMinWidths, maxColumnWidth, minColumnWidth, minTotalWidth, resizable, sumWidths])

  const handleResizeStart = useCallback((index, event) => {
    if (!resizable || event.button !== 0) return

    event.preventDefault()
    event.stopPropagation()

    const headerRow = tableRef.current?.querySelector('thead tr')
    const renderedWidths = Array.from(
      { length: headerRow?.children.length ?? 0 },
      (_, i) => {
        const th = headerRow?.children[i]
        const width = th?.getBoundingClientRect().width
        return (width && Number.isFinite(width)) ? width : minColumnWidth
      }
    )

    const startWidth = renderedWidths[index] ?? minColumnWidth
    const startX = event.clientX

    const previousCursor = document.body.style.cursor
    const previousUserSelect = document.body.style.userSelect
    document.body.style.cursor = "col-resize"
    document.body.style.userSelect = "none"

    const handlePointerMove = (moveEvent) => {
      const delta = moveEvent.clientX - startX
      const minimum = columnMinWidths[index] || minColumnWidth
      const nextWidth = Math.max(minimum, startWidth + delta)

      setColumnWidths(() => {
        const next = [...renderedWidths]
        next[index] = nextWidth
        return next
      })
    }

    const handlePointerUp = () => {
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerup", handlePointerUp)
      window.removeEventListener("pointercancel", handlePointerUp)
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = previousUserSelect
    }

    window.addEventListener("pointermove", handlePointerMove)
    window.addEventListener("pointerup", handlePointerUp)
    window.addEventListener("pointercancel", handlePointerUp)
    event.currentTarget.setPointerCapture?.(event.pointerId)
  }, [columnMinWidths, minColumnWidth, resizable])

  const handleSort = useCallback((accessorKey) => {
    const next = (() => {
      if (sortConfig.key !== accessorKey) return { key: accessorKey, direction: 'asc' }
      if (sortConfig.direction === 'asc') return { key: accessorKey, direction: 'desc' }
      return { key: null, direction: null }
    })()
    if (isControlled) {
      onSortChange?.(next)
    } else {
      setInternalSortConfig(next)
    }
  }, [sortConfig, isControlled, onSortChange])

  const sortedData = useMemo(() => {
    if (isControlled) return data
    if (!sortable || !sortConfig.key) return data
    const { key, direction } = sortConfig
    return [...data].sort((a, b) => {
      const aVal = a[key] ?? ''
      const bVal = b[key] ?? ''
      const result = String(aVal).localeCompare(String(bVal), undefined, { numeric: true, sensitivity: 'base' })
      return direction === 'asc' ? result : -result
    })
  }, [data, sortable, sortConfig, isControlled])

  const hasWidths = resizable && columnWidths.length === columns.length
  const isMeasuring = resizable && !hasWidths

  const totalWidth = hasWidths
    ? Math.max(sumWidths(columnWidths), minTotalWidth || 0)
    : undefined

  const tableContent = (
    <>
      {hasWidths && (
        <colgroup>
          {columnWidths.map((width, index) => (
            <col key={index} style={{ width: `${width}px` }} />
          ))}
        </colgroup>
      )}
      <TableHeader>
        <TableRow>
          {columns.map((column, index) => {
            const headerLabel = typeof column.header === "string" ? column.header : "column"
            const width = columnWidths[index]

            return (
              <TableHead
                key={column.accessorKey ?? column.header ?? index}
                className={cn(
                  resizable && "relative pr-3 select-none",
                  sortable && column.accessorKey && "p-0",
                  wrapCells && hasWidths && "whitespace-normal wrap-anywhere"
                )}
                style={resizable && width ? { width: `${width}px` } : undefined}
              >
                {sortable && column.accessorKey ? (
                  <button
                    type="button"
                    ref={(el) => { headerRefs.current[index] = el }}
                    onClick={() => handleSort(column.accessorKey)}
                    className="group/sort flex w-full h-full items-center gap-1 px-3 py-2 cursor-pointer hover:bg-muted transition-colors duration-150"
                  >
                    {column.header}
                    {sortConfig.key === column.accessorKey ? (
                      sortConfig.direction === 'asc'
                        ? <ChevronUp className="size-3.5 shrink-0 text-muted-foreground group-hover/sort:text-foreground transition-colors duration-150" />
                        : <ChevronDown className="size-3.5 shrink-0 text-muted-foreground group-hover/sort:text-foreground transition-colors duration-150" />
                    ) : (
                      <ChevronsUpDown className="size-3.5 shrink-0 text-muted-foreground opacity-0 group-hover/sort:text-foreground group-hover/sort:opacity-100 transition duration-150" />
                    )}
                  </button>
                ) : (
                  <span ref={(el) => { headerRefs.current[index] = el }} className="block w-full">
                    {column.header}
                  </span>
                )}
                {resizable && (
                  <div
                    role="separator"
                    aria-label={`Resize ${headerLabel} column`}
                    aria-orientation="vertical"
                    onPointerDown={(event) => handleResizeStart(index, event)}
                    className={cn("group absolute top-0 h-full w-3 cursor-col-resize select-none touch-none", index < columns.length - 1 ? "-right-1.5 z-10" : "right-0")}
                  >
                    <span className="absolute left-1/2 top-0 h-full w-px -translate-x-1/2 bg-border transition-[width] duration-150 ease-out group-hover:w-[3px]" />
                  </div>
                )}
              </TableHead>
            )
          })}
        </TableRow>
      </TableHeader>
      <TableBody>
        {sortedData.map((row, index) => (
          <TableRow
            key={row.id ?? row.key ?? index}
            onClick={onRowClick ? () => onRowClick(row) : undefined}
            className={onRowClick ? "cursor-pointer" : undefined}
          >
            {columns.map((column, columnIndex) => (
              <TableCell
                key={column.accessorKey ?? column.header ?? columnIndex}
                className={cn(wrapCells && hasWidths && "whitespace-normal wrap-anywhere align-top")}
                style={resizable && columnWidths[columnIndex] ? { width: `${columnWidths[columnIndex]}px` } : undefined}
              >
                {column.cell ? column.cell(row[column.accessorKey], row) : row[column.accessorKey]}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </>
  )

  const tableStyle = isMeasuring
    ? { width: 'max-content' }
    : resizable && totalWidth
      ? { width: `${totalWidth}px`, minWidth: '100%' }
      : undefined

  return (
    <div className={cn("w-full rounded-lg border border-border overflow-hidden [contain:inline-size]", className)}>
      <ScrollArea orientation="horizontal">
        <Table
          containerClassName="border-0 rounded-none overflow-visible"
          ref={tableRef}
          className={cn(hasWidths && "table-fixed")}
          style={tableStyle}
        >
          {tableContent}
        </Table>
      </ScrollArea>
    </div>
  )
}
